import React, { useState, useRef, useEffect, useCallback } from 'react'
import { CompactPicker } from 'react-color'
import styles from './FloatingToolbar.module.scss'

// Font options - this should match what's available in your system
const FONT_OPTIONS = [
  'Arial',
  'Avenir Next LT Pro',
  'AvenirNextLTPro',
  'Barcode',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
]

export default function FloatingToolbar({
  field,
  onUpdate,
  onCopySettings,
  onPasteSettings,
  hasCopiedFormatting = false,
  position,
  fonts = []
}) {
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [toolbarPosition, setToolbarPosition] = useState({ x: position?.x || 100, y: position?.y || 100 })
  const [hasBeenDragged, setHasBeenDragged] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [showFontDropdown, setShowFontDropdown] = useState(false)
  const [showSpacingPopup, setShowSpacingPopup] = useState(false)
  const [spacingInput, setSpacingInput] = useState('')
  const [isEditingSpacing, setIsEditingSpacing] = useState(false)
  const [fontSizeInput, setFontSizeInput] = useState('')
  const [isEditingFontSize, setIsEditingFontSize] = useState(false)
  const [placeholderInput, setPlaceholderInput] = useState('')
  const [isEditingPlaceholder, setIsEditingPlaceholder] = useState(false)
  const toolbarRef = useRef(null)
  const colorPickerRef = useRef(null)
  const fontDropdownRef = useRef(null)
  const spacingPopupRef = useRef(null)
  const lastPositionRef = useRef(null)

  // Reset dragged state when field changes (position changes significantly)
  const prevFieldIdRef = useRef(field?.id)
  useEffect(() => {
    if (field?.id !== prevFieldIdRef.current) {
      prevFieldIdRef.current = field?.id
      setHasBeenDragged(false)
      lastPositionRef.current = null
    }
  }, [field?.id])

  // Only update position when it's a new selection
  // Don't reposition if user has manually dragged the toolbar
  useEffect(() => {
    if (position && !hasBeenDragged) {
      const posKey = `${Math.round(position.x)}-${Math.round(position.y)}`
      if (posKey !== lastPositionRef.current) {
        lastPositionRef.current = posKey
        setToolbarPosition({ x: position.x, y: position.y })
      }
    }
  }, [position?.x, position?.y, hasBeenDragged])

  // Handle dragging
  const handleMouseDown = (e) => {
    if (e.target.closest(`.${styles.toolbarHandle}`)) {
      setIsDragging(true)
      const rect = toolbarRef.current.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
    }
  }

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setToolbarPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      })
    }
  }, [isDragging, dragOffset])

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setHasBeenDragged(true)
    }
    setIsDragging(false)
  }, [isDragging])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target)) {
        setShowFontDropdown(false)
      }
      if (spacingPopupRef.current && !spacingPopupRef.current.contains(e.target)) {
        setShowSpacingPopup(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!field) return null

  const fontOptions = fonts.length > 0
    ? fonts.map(f => ({ label: f.name, value: f.id }))
    : FONT_OPTIONS.map(f => ({ label: f, value: f }))

  const currentFontName = field.fontName || 'Arial'
  const currentFontSize = field.textSize || 16

  const handleFontChange = (fontName, fontId) => {
    onUpdate({ fontName, fontId })
    setShowFontDropdown(false)
  }

  const handleFontSizeChange = (delta) => {
    const newSize = Math.max(8, Math.min(200, currentFontSize + delta))
    onUpdate({ textSize: newSize })
  }

  const handleFontSizeInputChange = (e) => {
    setFontSizeInput(e.target.value)
  }

  const handleFontSizeFocus = () => {
    setIsEditingFontSize(true)
    setFontSizeInput(currentFontSize.toString())
  }

  const handleFontSizeBlur = () => {
    setIsEditingFontSize(false)
    const value = parseInt(fontSizeInput, 10)
    if (!isNaN(value) && value >= 8 && value <= 200) {
      onUpdate({ textSize: value })
    }
    setFontSizeInput('')
  }

  const handleFontSizeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      setIsEditingFontSize(false)
      setFontSizeInput('')
    }
  }

  // Placeholder text handlers
  const currentPlaceholder = field.sampleText || ''

  const handlePlaceholderChange = (e) => {
    setPlaceholderInput(e.target.value)
  }

  const handlePlaceholderFocus = () => {
    setIsEditingPlaceholder(true)
    setPlaceholderInput(currentPlaceholder)
  }

  const handlePlaceholderBlur = () => {
    setIsEditingPlaceholder(false)
    if (placeholderInput !== currentPlaceholder) {
      onUpdate({ sampleText: placeholderInput })
    }
    setPlaceholderInput('')
  }

  const handlePlaceholderKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      setIsEditingPlaceholder(false)
      setPlaceholderInput('')
    }
  }

  const currentSpacing = field.spacing || 0

  const handleSpacingSliderChange = (e) => {
    const value = parseInt(e.target.value, 10)
    onUpdate({ spacing: value })
  }

  const handleSpacingInputFocus = () => {
    setIsEditingSpacing(true)
    setSpacingInput(currentSpacing.toString())
  }

  const handleSpacingInputChange = (e) => {
    setSpacingInput(e.target.value)
  }

  const handleSpacingInputBlur = () => {
    setIsEditingSpacing(false)
    const value = parseInt(spacingInput, 10)
    if (!isNaN(value) && value >= -10 && value <= 40) {
      onUpdate({ spacing: value })
    }
    setSpacingInput('')
  }

  const handleSpacingInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      setIsEditingSpacing(false)
      setSpacingInput('')
    }
  }

  const handleSpacingReset = () => {
    onUpdate({ spacing: 0 })
  }

  const handleColorChange = (color) => {
    onUpdate({ color: color.hex })
  }

  const handleAlignChange = (align) => {
    onUpdate({ align })
  }

  const handleWrapToggle = () => {
    onUpdate({ wrap: !field.wrap })
  }

  const handleAutoFitToggle = () => {
    onUpdate({ autoFit: !field.autoFit })
  }

  const handleEditableChange = (e) => {
    const isEditable = e.target.value === 'editable'
    onUpdate({ customerEditable: isEditable })
  }

  const handleBoldToggle = () => {
    const currentStyle = field.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')

    let newStyle = 'normal'
    if (!isBold && isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'bold'
    else if (isBold && isItalic) newStyle = 'italic'
    // else isBold && !isItalic -> 'normal'

    onUpdate({ fontStyle: newStyle })
  }

  const handleItalicToggle = () => {
    const currentStyle = field.fontStyle || 'normal'
    const isBold = currentStyle.includes('bold')
    const isItalic = currentStyle.includes('italic')

    let newStyle = 'normal'
    if (isBold && !isItalic) newStyle = 'bold italic'
    else if (!isBold && !isItalic) newStyle = 'italic'
    else if (isBold && isItalic) newStyle = 'bold'
    // else !isBold && isItalic -> 'normal'

    onUpdate({ fontStyle: newStyle })
  }

  const handleUnderlineToggle = () => {
    const currentDecoration = field.textDecoration || ''
    const hasUnderline = currentDecoration.includes('underline')

    if (hasUnderline) {
      onUpdate({ textDecoration: currentDecoration.replace('underline', '').trim() })
    } else {
      onUpdate({ textDecoration: (currentDecoration + ' underline').trim() })
    }
  }

  // Prevent clicks on toolbar from propagating to canvas/page
  const handleToolbarClick = (e) => {
    e.stopPropagation()
  }

  return (
    <div
      ref={toolbarRef}
      className={styles.floatingToolbar}
      style={{
        left: toolbarPosition.x,
        top: toolbarPosition.y,
      }}
      onMouseDown={handleMouseDown}
      onClick={handleToolbarClick}
    >
      {/* Drag handle */}
      <div className={styles.toolbarHandle} title="Drag to move">
        <span className={styles.dragIcon}>⋮⋮</span>
      </div>

      {/* Editable/Locked selector */}
      <select
        className={styles.editableSelect}
        value={field.customerEditable === true ? 'editable' : 'locked'}
        onChange={handleEditableChange}
        title="Customer can edit this field"
      >
        <option value="editable">Editable</option>
        <option value="locked">Locked</option>
      </select>

      <div className={styles.divider} />

      {/* Placeholder text input - only show in admin mode (when field has database object) */}
      {field.object && (
        <>
          <input
            type="text"
            className={styles.placeholderInput}
            value={isEditingPlaceholder ? placeholderInput : currentPlaceholder}
            onChange={handlePlaceholderChange}
            onFocus={handlePlaceholderFocus}
            onBlur={handlePlaceholderBlur}
            onKeyDown={handlePlaceholderKeyDown}
            placeholder="Placeholder text"
            title="Sample/placeholder text shown in preview"
          />

          <div className={styles.divider} />
        </>
      )}

      {/* Font selector */}
      <div className={styles.fontSelector} ref={fontDropdownRef}>
        <button
          type="button"
          className={styles.fontButton}
          onClick={() => setShowFontDropdown(!showFontDropdown)}
        >
          <span className={styles.fontName}>{currentFontName}</span>
          <span className={styles.dropdownArrow}>▼</span>
        </button>
        {showFontDropdown && (
          <div className={styles.fontDropdown}>
            {fontOptions.map((font) => (
              <div
                key={font.value}
                className={styles.fontOption}
                style={{ fontFamily: font.label }}
                onClick={() => handleFontChange(font.label, font.value)}
              >
                {font.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Font size */}
      <div className={styles.fontSizeControl}>
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(-1)}
        >
          −
        </button>
        <input
          type="text"
          className={styles.sizeInput}
          value={isEditingFontSize ? fontSizeInput : currentFontSize}
          onChange={handleFontSizeInputChange}
          onFocus={handleFontSizeFocus}
          onBlur={handleFontSizeBlur}
          onKeyDown={handleFontSizeKeyDown}
        />
        <button
          type="button"
          className={styles.sizeButton}
          onClick={() => handleFontSizeChange(1)}
        >
          +
        </button>
      </div>

      <div className={styles.divider} />

      {/* Text color */}
      <div className={styles.colorControl} ref={colorPickerRef}>
        <button
          type="button"
          className={styles.colorButton}
          onClick={() => setShowColorPicker(!showColorPicker)}
          title="Text color"
        >
          <span className={styles.colorIcon}>A</span>
          <span
            className={styles.colorBar}
            style={{ backgroundColor: field.color || '#000000' }}
          />
        </button>
        {showColorPicker && (
          <div className={styles.colorPickerPopup}>
            <CompactPicker
              color={field.color || '#000000'}
              onChange={handleColorChange}
            />
          </div>
        )}
      </div>

      {/* Letter spacing */}
      <div className={styles.spacingControl} ref={spacingPopupRef}>
        <button
          type="button"
          className={styles.spacingButton}
          onClick={() => setShowSpacingPopup(!showSpacingPopup)}
          title="Letter spacing"
        >
          <svg width="20" height="16" viewBox="0 0 20 16">
            <text x="2" y="12" fontSize="11" fontWeight="bold" fill="currentColor">A</text>
            <text x="11" y="12" fontSize="11" fontWeight="bold" fill="currentColor">V</text>
            <path d="M4 14h12" stroke="currentColor" strokeWidth="1" strokeDasharray="1,1.5" />
          </svg>
        </button>
        {showSpacingPopup && (
          <div className={styles.spacingPopup}>
            <div className={styles.spacingPopupHeader}>
              <span className={styles.spacingLabel}>Letter spacing</span>
              <button
                type="button"
                className={styles.spacingResetButton}
                onClick={handleSpacingReset}
                title="Reset to 0"
              >
                Reset
              </button>
            </div>
            <div className={styles.spacingSliderRow}>
              <input
                type="range"
                className={styles.spacingSlider}
                min="-10"
                max="40"
                step="1"
                value={currentSpacing}
                onChange={handleSpacingSliderChange}
              />
              <input
                type="text"
                className={styles.spacingNumberInput}
                value={isEditingSpacing ? spacingInput : currentSpacing}
                onChange={handleSpacingInputChange}
                onFocus={handleSpacingInputFocus}
                onBlur={handleSpacingInputBlur}
                onKeyDown={handleSpacingInputKeyDown}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.divider} />

      {/* Text formatting */}
      <button
        type="button"
        className={`${styles.formatButton} ${(field.fontStyle || '').includes('bold') ? styles.active : ''}`}
        title="Bold"
        onClick={handleBoldToggle}
      >
        <strong>B</strong>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${(field.fontStyle || '').includes('italic') ? styles.active : ''}`}
        title="Italic"
        onClick={handleItalicToggle}
      >
        <em>I</em>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${(field.textDecoration || '').includes('underline') ? styles.active : ''}`}
        title="Underline"
        onClick={handleUnderlineToggle}
      >
        <span style={{ textDecoration: 'underline' }}>U</span>
      </button>

      <div className={styles.divider} />

      {/* Alignment */}
      <button
        type="button"
        className={`${styles.formatButton} ${field.align === 'left' ? styles.active : ''}`}
        onClick={() => handleAlignChange('left')}
        title="Align left"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM2 6h8v1H2zM2 9h12v1H2zM2 12h8v1H2z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${field.align === 'center' ? styles.active : ''}`}
        onClick={() => handleAlignChange('center')}
        title="Align center"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM4 6h8v1H4zM2 9h12v1H2zM4 12h8v1H4z"/>
        </svg>
      </button>
      <button
        type="button"
        className={`${styles.formatButton} ${field.align === 'right' ? styles.active : ''}`}
        onClick={() => handleAlignChange('right')}
        title="Align right"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM6 6h8v1H6zM2 9h12v1H2zM6 12h8v1H6z"/>
        </svg>
      </button>

      <div className={styles.divider} />

      {/* Wrap */}
      <button
        type="button"
        className={`${styles.formatButton} ${field.wrap ? styles.active : ''}`}
        onClick={handleWrapToggle}
        title="Text wrap"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M2 3h12v1H2zM2 6h10v1H2zM12 6v3H8v1h5V6zM2 9h5v1H2zM2 12h12v1H2z"/>
        </svg>
      </button>

      {/* Auto-fit */}
      <button
        type="button"
        className={`${styles.formatButton} ${field.autoFit ? styles.active : ''}`}
        onClick={handleAutoFitToggle}
        title="Auto-fit text (shrink to fit)"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M1 1h14v14H1V1zm1 1v12h12V2H2zm2 3h8v1H4V5zm0 2h8v1H4V7zm0 2h5v1H4V9z"/>
          <path fill="currentColor" d="M11 9l2 2-2 2v-1.5H9v-1h2V9z"/>
        </svg>
      </button>

      {/* Copy settings */}
      <button
        type="button"
        className={styles.formatButton}
        onClick={() => onCopySettings && onCopySettings(field)}
        title="Copy formatting"
      >
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path fill="currentColor" d="M4 2v10h8V2H4zm7 9H5V3h6v8zM2 4v10h8v-1H3V4H2z"/>
        </svg>
      </button>

      {/* Paste settings - only show when formatting has been copied */}
      {hasCopiedFormatting && (
        <button
          type="button"
          className={styles.formatButton}
          onClick={() => onPasteSettings && onPasteSettings()}
          title="Paste formatting (click to apply copied settings)"
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            border: '2px solid #388E3C',
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path fill="white" d="M10 2H4v10h8V4l-2-2zm1 9H5V3h4v2h2v6zM2 4v10h8v-1H3V4H2z"/>
            <path fill="white" d="M7 6v2H5v1h2v2h1V9h2V8H8V6H7z"/>
          </svg>
        </button>
      )}
    </div>
  )
}
